import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Checkbox,
  CircularProgress,
  TableSortLabel,
  Menu,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import debounce from "lodash.debounce";
import styled from "styled-components";
import StyledTextField from "./StyledTextField";
import { MoreVert } from "@mui/icons-material";

const StyledPaper = styled(Paper)`
  background-color: #2b3342 !important;
  color: #fbfeff !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StickyTableContainer = styled.div`
  background-color: #2b3342 !important;
  box-shadow: none !important;
  max-height: ${(props) => props.height || "calc(100vh - 300px)"} !important;
  overflow-y: auto !important;
`;

export default function TableWithPagination({
  title,
  columns = [],
  rows = [],
  isLoading = false,
  noSearch = false,
  onSearchChange = () => {},
  onClearSearch = () => {},
  totalRecords = rows.length,
  page = 0,
  limit = 10,
  onPageChange = () => {},
  onLimitChange = () => {},
  onRefresh = () => {},
  onRowClick = () => {},
  onSort = () => {},
  selectableRows = false,
  onSelectedRowsChange = () => {},
  width,
  onActionClick = () => {},
  preSelectedRows = [],
  height = "calc(100vh - 300px)",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedRows, setSelectedRows] = useState([]);

  const [menuState, setMenuState] = useState({
    anchorEl: null,
    row: null,
  });

  useEffect(() => {
    if (preSelectedRows && preSelectedRows.length > 0) {
      setSelectedRows(preSelectedRows);
    }
  }, [preSelectedRows]);
  

  const debouncedSearch = React.useMemo(
    () =>
      debounce((val) => {
        onSearchChange(val);
      }, 500),
    [onSearchChange]
  );

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedSearch(val);
    if (!val) {
      onClearSearch();
    }
  };

  const handleClearAll = () => {
    setSearchTerm("");
    setSortConfig({ key: null, direction: "asc" });
    setSelectedRows([]);
    onClearSearch();
  };

  const handleSort = (columnId) => {
    const newDirection =
      sortConfig.key === columnId && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ key: columnId, direction: newDirection });
    onSort(columnId, newDirection);
  };

  const handleRowSelect = (row) => {
    if (!selectableRows) return;
    if (selectedRows.some((r) => r.id === row.id)) {
      const updated = selectedRows.filter((r) => r.id !== row.id);
      setSelectedRows(updated);
      onSelectedRowsChange(updated);
    } else {
      const updated = [...selectedRows, row];
      setSelectedRows(updated);
      onSelectedRowsChange(updated);
    }
  };

  const handleSelectAll = () => {
    if (!selectableRows) return;
    if (selectedRows.length === rows.length) {
      setSelectedRows([]);
      onSelectedRowsChange([]);
    } else {
      setSelectedRows(rows);
      onSelectedRowsChange(rows);
    }
  };

  const renderCellValue = (col, row) => {
    if (col.type === "boolean") {
      return row[col.id] ? "Yes" : "No";
    }
    return row[col.id] ?? "-";
  };

  const handleMenuOpen = (row, event) => {
    event.stopPropagation();
    setMenuState({
      anchorEl: event.currentTarget,
      row,
    });
  };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      row: null,
    });
  };

  function renderActionMenu(col, row) {
    const open = Boolean(menuState.anchorEl) && menuState.row === row;

    return (
      <>
        <IconButton
          onClick={(e) => handleMenuOpen(row, e)}
          sx={{ color: "white" }}
        >
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={menuState.anchorEl}
          open={open}
          onClose={handleMenuClose}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: "#000000",
              color: "#ffffff",
            },
          }}
        >
          {col.buttons.map((btn) => (
            <MenuItem
              key={btn.id}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose();
                onActionClick(btn.id, menuState.row);
              }}
            >
              {btn.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <StyledPaper sx={{ padding: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">{title}</Typography>
        <Box display="flex" alignItems="center">
          <IconButton onClick={onRefresh} sx={{ marginLeft: 1 }}>
            <RefreshIcon sx={{ color: "white" }} />
          </IconButton>
          {(searchTerm || sortConfig.key || selectedRows.length > 0) && (
            <Box ml={2}>
              <Button
                variant="contained"
                color="error"
                onClick={handleClearAll}
              >
                Clear
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {!noSearch && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <StyledTextField
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{ startAdornment: <SearchIcon /> }}
            sx={{ width: "200px" }}
          />
        </Box>
      )}

      <StickyTableContainer height={height} style={{ width: width || "100%" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {selectableRows && (
                <TableCell>
                  <Checkbox
                    checked={
                      selectedRows.length === rows.length && rows.length > 0
                    }
                    onChange={handleSelectAll}
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      "&.MuiCheckbox-root": {
                        visibility: "visible !important",
                      },
                    }}
                  />
                </TableCell>
              )}
              {columns
                .filter((col) => col.display !== "hidden")
                .map((col) => {
                  if (!col.buttons) {
                    return (
                      <TableCell key={col.id} sx={{ color: "white" }}>
                        {onSort ? (
                          <TableSortLabel
                            active={sortConfig.key === col.id}
                            direction={
                              sortConfig.key === col.id
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort(col.id)}
                            sx={{ color: "white !important" }}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={col.id} sx={{ color: "white" }}>
                      {col.label}
                    </TableCell>
                  );
                })}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow key="loading">
                <TableCell
                  colSpan={
                    columns.filter((col) => col.display !== "hidden").length +
                    (selectableRows ? 1 : 0)
                  }
                  align="center"
                >
                  <CircularProgress sx={{ color: "white" }} />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedRows.some((r) => r.id === row.id)}
                  onClick={() => {
                    onRowClick(row);
                    handleRowSelect(row);
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  {selectableRows && (
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.some((r) => r.id === row.id)}
                        sx={{
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                          "&.MuiCheckbox-root": {
                            visibility: "visible !important",
                          },
                        }}
                      />
                    </TableCell>
                  )}
                  {columns
                    .filter((col) => col.display !== "hidden")
                    .map((col) => {
                      if (col.buttons) {
                        return (
                          <TableCell key={col.id}>
                            {renderActionMenu(col, row)}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={col.id}>
                          {renderCellValue(col, row)}
                        </TableCell>
                      );
                    })}
                </TableRow>
              ))
            ) : (
              <TableRow key={"no-data"}>
                <TableCell
                  colSpan={
                    columns.filter((col) => col.display !== "hidden").length +
                    (selectableRows ? 1 : 0)
                  }
                  align="center"
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableContainer>

      <Box mt={1}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={limit}
          page={page}
          onPageChange={(event, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(event) =>
            onLimitChange(parseInt(event.target.value, 10))
          }
          slotProps={{
            select: {
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#141b25",
                    color: "#FBFEFF",
                  },
                },
              },
            },
          }}
        />
      </Box>
    </StyledPaper>
  );
}
